import React, { useState, useEffect } from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import { IoIosAddCircle } from "react-icons/io";
import { MdRemoveCircle } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { addProductToCart, updateCartItemQuantity, removeProductFromCart } from "../redux/cartSlice";
import { addProductToWishlist, removeProductFromWishlist } from "../redux/WishlistSlice";
import { getFallbackImage } from "../utils/imageLoader";
import { getFullImageUrl } from "../api";
import ProductModal from "./ProductModal";
import styles from "./Card.module.css";

//  card component
const Card = React.forwardRef(({ post, isWishlistPage = false, onClick }, ref) => {
  // State for modal and image index
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const dispatch = useDispatch();
  // Redux selectors for wishlist and cart
  const wishlistItems = useSelector(state => state.wishlist.items);
  const cartItems = useSelector(state => state.cart.items);
  // Check if product is in wishlist
  const isInWishlist = post ? wishlistItems.some(item => item.id === post._id) : false;
  const currentItem = post ? cartItems.find(item => item.id === post._id) : null;
  const quantity = currentItem ? currentItem.quantity : 0;
  // Prepare images for carousel
  const images = post?.images?.length > 0 ? post.images.map(getFullImageUrl) : [];
  // Determine if multiple images exist
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    // Auto slide for multiple images
    if (hasMultipleImages) {
      // Set interval for auto sliding
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
      }, 5000);
      // Cleanup interval on unmount
      return () => clearInterval(interval);
    }
  }, [hasMultipleImages, images.length]);
  // Get price from first variant
  const price = post?.variants?.[0]?.price || 0;
  // Handle add to cart action
  const handleAddToCart = () => {
    if (!post?.variants?.length) return;
    // Add the first variant of the product to the cart
    const firstVariant = post.variants[0];
    const safeImage = images[0] || getFallbackImage();

    // Dispatch action to add product to cart with selected variant and details
    dispatch(addProductToCart({
      ...post,
      id: post._id,
      price: firstVariant.price,
      image: safeImage,
      title: post.name,
      variantId: firstVariant._id,
      selectedColor: firstVariant.color,
      selectedSize: firstVariant.size
    }, 1));
  };
  // Handle increment quantity
  const handleIncrement = () => {
    if (currentItem) dispatch(updateCartItemQuantity(currentItem, quantity + 1));
  };
  //  Handle decrement quantity
  const handleDecrement = () => {
    if (currentItem) {
      // If quantity is 1, remove item from cart
      if (quantity === 1) {
        dispatch(removeProductFromCart(currentItem));
      } else {
        dispatch(updateCartItemQuantity(currentItem, quantity - 1));
      }
    }
  };
  // Toggle wishlist status
  const toggleWishlist = () => {
    // Toggle product in wishlist
    const safeImage = images[0] || getFallbackImage();
    if (isInWishlist) {
      // Remove product from wishlist
      const wishlistItem = wishlistItems.find(item => item.id === post._id);
      if (wishlistItem) dispatch(removeProductFromWishlist(wishlistItem));
    } else {
      // Add product to wishlist
      dispatch(addProductToWishlist({
        ...post,
        id: post._id,
        price,
        image: safeImage,
        title: post.name
      }));
    }
  };
  // Handle remove from wishlist 
  const handleRemoveFromWishlist = () => {
    const wishlistItem = wishlistItems.find(item => item.id === post._id);
    if (wishlistItem) dispatch(removeProductFromWishlist(wishlistItem));
  };
  // Return null if no post
  if (!post) return null;

  return (
    <>
      <div className={styles.card} data-aos="fade-up" onClick={onClick} ref={ref}>
        {isWishlistPage && (
          <button className={styles.removeWishlistButton}
            onClick={(e) => { e.stopPropagation(); handleRemoveFromWishlist(); }}>
            <FaTrash />
          </button>
        )}
        {/* Image container */}
        <div className={styles.imageContainer}>
          {hasMultipleImages ? (
            <div style={{ display: "flex", transition: "transform 0.5s ease", transform: `translateX(-${currentImageIndex * 100}%)` }}>
              {images.map((imgUrl, idx) => (
                <img key={idx} src={imgUrl} alt={`${post.name}-${idx}`}
                  className={styles.image}
                  onClick={(e) => { e.stopPropagation(); setModalOpen(true); }} />
              ))}
            </div>
          ) : (
            <img src={images[0] || getFallbackImage()} alt={post.name}
              className={styles.image}
              onClick={(e) => { e.stopPropagation(); setModalOpen(true); }} />
          )}
        </div>
        <p className={styles.title} onClick={() => setModalOpen(true)} style={{ cursor: "pointer" }}>{post.name}</p>

        <div className={styles.priceWishlistContainer}>
          <p className={styles.price}>₹{price.toFixed(2)}</p>

          {!isWishlistPage && (
            <>
              {quantity === 0 ? (
                <button className={styles.button}
                  onClick={(e) => { e.stopPropagation(); handleAddToCart(); }}>
                  Add to Cart
                </button>
              ) : (
                <div className={styles.quantityContainer}>
                  <button className={styles.quantityButton}
                    onClick={(e) => { e.stopPropagation(); handleIncrement(); }}>
                    <IoIosAddCircle />
                  </button>
                  <span className={styles.quantity}>{quantity}</span>
                  <button className={styles.quantityButton}
                    onClick={(e) => { e.stopPropagation(); handleDecrement(); }}>
                    <MdRemoveCircle />
                  </button>
                </div>
              )}

              <button className={styles.wishlistButton}
                onClick={(e) => { e.stopPropagation(); toggleWishlist(); }}>
                {isInWishlist ? <AiFillHeart /> : <AiOutlineHeart />}
              </button>
            </>
          )}
        </div>
      </div>
      <ProductModal post={post} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
});

export default Card;
